import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import { FaMapMarkerAlt } from "react-icons/fa";
import dayjs from "dayjs";
import { useHomeContext } from "../../../context/Home_context";
import { buildYouTubeBackgroundEmbedUrl } from "../../../utils/helper";
import "../../home/homehero/HomeHero.css";
import { formatExpoTitle } from "../../home/homehero/HomeHero";

const ShowLogo = ({ show }) => {
  const imgSrc = show.image_full_path || "";
  const name = show.name || "";
  const link = show.link || null;

  if (!imgSrc) return null;

  const content = <img src={imgSrc} alt={name} loading="lazy" />;

  if (link) {
    return (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="hh-show-card"
      >
        {content}
      </a>
    );
  }

  return <div className="hh-show-card">{content}</div>;
};

const EditionHero = ({ data }) => {
  const { edition_expo_data } = useHomeContext();
  const expoData = data || edition_expo_data || {};

  // Build embeddable YouTube background URL from API
  const youtubeEmbedUrl = useMemo(
    () => buildYouTubeBackgroundEmbedUrl(expoData?.unlock_sucess_link),
    [expoData?.unlock_sucess_link],
  );

  // Format dates & venue dynamically using dayjs
  const expoDates = useMemo(() => {
    const fromDate = expoData?.from_date ? dayjs(expoData.from_date) : null;

    const toDate = expoData?.to_date ? dayjs(expoData.to_date) : null;

    if (!fromDate || !toDate || !fromDate.isValid() || !toDate.isValid()) {
      return null;
    }

    const isSameDay = fromDate.isSame(toDate, "day");
    const isSameMonth = fromDate.isSame(toDate, "month");

    return {
      fromDay: fromDate.format("DD"),
      toDay: toDate.format("DD"),
      isRange: !isSameDay,
      isSameMonth,
      fromMonth: fromDate.format("MMMM").toUpperCase(),
      toMonth: toDate.format("MMMM").toUpperCase(),
      fromYear: fromDate.format("YYYY"),
      toYear: toDate.format("YYYY"),
    };
  }, [expoData?.from_date, expoData?.to_date]);

  const venueName = expoData?.venue_name || "";

  const venueLink = useMemo(() => {
    const rawLink = expoData?.venue_link;
    if (!rawLink) return "";
    const match = String(rawLink).match(/https?:\/\/[^\s"']+/);
    return match ? match[0] : String(rawLink).trim();
  }, [expoData?.venue_link]);

  const heroLogo = expoData?.logo_full_path || expoData?.logo || "";

  const welcomeText = useMemo(() => {
    const raw = expoData?.expo_title;
    if (!raw) return "";
    return String(raw)
      .replace(/<[^>]*>/g, "")
      .trim();
  }, [expoData?.expo_title]);

  const galleryList = expoData?.gallerys || [];

  const marqueeItems = useMemo(() => {
    if (!galleryList.length) return [];

    const minItems = 12;
    const repeatCount = Math.ceil(minItems / galleryList.length);

    return Array.from({ length: repeatCount }, () => galleryList).flat();
  }, [expoData?.gallerys]);

  return (
    <section className="hh-hero">
      {/* Video background */}
      <div className="hh-video-wrap" aria-hidden="true">
        {youtubeEmbedUrl ? (
          <iframe
            className="hh-video hh-video-yt"
            src={youtubeEmbedUrl}
            title="Bharat Buildcon Background Video"
            frameBorder="0"
            allow="autoplay; encrypted-media; fullscreen"
            allowFullScreen
          />
        ) : (
          <div className="hh-video-fallback" />
        )}
        <div className="hh-video-scrim" />
      </div>

      {/* Main content */}
      <div className="hh-content">
        {welcomeText && (
          <span className="hh-welcome-kicker">{welcomeText}</span>
        )}

        {heroLogo && (
          <div className="hh-hero-logos-wrapper">
            <Link
              to="/"
              className="hh-hero-logo"
              aria-label={expoData?.name || "Bharat Buildcon Edition"}
            >
              <img
                className="hh-hero-logo-img"
                src={heroLogo}
                alt={expoData?.name || "Bharat Buildcon"}
                draggable="false"
                onContextMenu={(e) => e.preventDefault()}
              />
            </Link>
          </div>
        )}

        {expoData?.expo_desc && (
          <div className="hh-slogan-box">
            <h2 className="hh-slogan-main">
              {formatExpoTitle(expoData.expo_desc)}
            </h2>
          </div>
        )}

        {(expoDates || venueName) && (
          <div className="hh-dates">
            <div className="hh-date-block">
              <div className="hh-date-range">
                {expoDates.isRange ? (
                  expoDates.isSameMonth ? (
                    <>
                      <span className="hh-date-box notranslate" translate="no">
                        {expoDates.fromDay}
                      </span>

                      <span className="hh-date-dash">–</span>

                      <span className="hh-date-box notranslate" translate="no">
                        {expoDates.toDay}
                      </span>

                      <span className="hh-date-month">
                        {expoDates.fromMonth}{" "}
                        <span className="notranslate" translate="no">
                          {expoDates.fromYear}
                        </span>
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="hh-date-box notranslate" translate="no">
                        {expoDates.fromDay}
                      </span>

                      <span className="hh-date-month">
                        {expoDates.fromMonth}
                      </span>

                      <span className="hh-date-dash">–</span>

                      <span className="hh-date-box notranslate" translate="no">
                        {expoDates.toDay}
                      </span>

                      <span className="hh-date-month">
                        {expoDates.toMonth}{" "}
                        <span className="notranslate" translate="no">
                          {expoDates.toYear}
                        </span>
                      </span>
                    </>
                  )
                ) : (
                  <>
                    <span className="hh-date-box notranslate" translate="no">
                      {expoDates.fromDay}
                    </span>

                    <span className="hh-date-month">
                      {expoDates.fromMonth}{" "}
                      <span className="notranslate" translate="no">
                        {expoDates.fromYear}
                      </span>
                    </span>
                  </>
                )}
              </div>
              {venueName &&
                (venueLink ? (
                  <a
                    href={venueLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hh-date-venue"
                  >
                    <FaMapMarkerAlt className="hh-pin" />
                    {venueName}
                  </a>
                ) : (
                  <span className="hh-date-venue">
                    <FaMapMarkerAlt className="hh-pin" />
                    {venueName}
                  </span>
                ))}
            </div>
          </div>
        )}

        <div className="hh-cta-row hh-cta-row--center">
          <Link to="/exhibitors" className="hh-btn hh-btn--solid hh-btn--lg">
            Book your stall{" "}
          </Link>
          <Link to="/registration" className="hh-btn hh-btn--blue hh-btn--lg">
            Register as Visitor
          </Link>
        </div>
      </div>

      {marqueeItems.length > 0 && (
        <div className="hh-marquee-shell">
          <div className="hh-marquee-viewport">
            <Swiper
              modules={[Autoplay, FreeMode]}
              loop={true}
              speed={6000}
              autoplay={{
                delay: 0,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              freeMode={{
                enabled: true,
                momentum: false,
              }}
              slidesPerView="auto"
              spaceBetween={22}
              allowTouchMove={true}
              className="hh-swiper-marquee"
            >
              {marqueeItems.map((show, index) => (
                <SwiperSlide
                  key={`${show.id || show.name}-${index}`}
                  className="hh-swiper-slide"
                >
                  <ShowLogo show={show} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      )}
    </section>
  );
};

export default EditionHero;
